import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Play } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Field, FieldDescription, FieldGroup, FieldLabel,
} from '@/components/ui/field'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE, extractErr, useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type EventDef = {
  id: number
  code: string
  label: string
  category: string
  description: string
  placeholders: string[]
  default_link_template: string
}

type UserLite = { id: number; username: string; first_name: string; last_name: string; email: string }
type RoleLite = { id: number; name: string }

type Rule = {
  id: number
  name: string
  description: string
  trigger_event: number
  trigger_event_code: string
  trigger_event_label: string
  trigger_event_category: string
  recipient_kind: 'USER' | 'ROLE' | 'DYNAMIC'
  recipient_users: number[]
  recipient_roles: number[]
  recipient_resolver: string
  channels: string[]
  subject_template: string
  body_text_template: string
  body_html_template: string
  link_template: string
  filter_json: Record<string, any>
  is_active: boolean
  is_system: boolean
}

const RESOLVERS = [
  { value: 'actor_self', label: 'The user who triggered the event (actor_self)' },
  { value: 'actor_manager', label: "Actor's direct manager (actor_manager)" },
  { value: 'event_field:', label: 'Read user id from a context field — append the key, e.g. event_field:inviter_user_id' },
]

const CHANNELS = [
  { value: 'IN_APP', label: 'In-app (bell icon)' },
  { value: 'EMAIL', label: 'Email' },
]

// ─────────────────── Page ───────────────────

export default function AdminNotificationRuleDetail() {
  const { id } = useParams<{ id: string }>()
  const ruleId = id ? Number(id) : 0
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const ruleQ = useQuery({
    queryKey: ['notif-rule', ruleId],
    queryFn: async () => (await api.get<Rule>(`/notifications/rules/${ruleId}/`)).data,
    enabled: !!ruleId,
  })
  const eventsQ = useQuery({
    queryKey: ['notif-events'],
    queryFn: async () => (await api.get<EventDef[]>('/notifications/events/')).data,
  })
  const usersQ = useQuery({
    queryKey: ['core', 'users'],
    queryFn: async () => (await api.get<UserLite[]>('/users/')).data,
    retry: false,
  })
  const rolesQ = useQuery({
    queryKey: ['core', 'roles'],
    queryFn: async () => (await api.get<RoleLite[]>('/roles/')).data,
    retry: false,
  })

  // Local draft mirroring the rule.
  const [draft, setDraft] = useState<Rule | null>(null)
  useEffect(() => { if (ruleQ.data) setDraft(ruleQ.data) }, [ruleQ.data])

  const [filterJsonRaw, setFilterJsonRaw] = useState<string>('')
  useEffect(() => {
    if (ruleQ.data) {
      setFilterJsonRaw(JSON.stringify(ruleQ.data.filter_json || {}, null, 2))
    }
  }, [ruleQ.data])

  const saveMut = useMutation({
    mutationFn: async (payload: Partial<Rule>) =>
      (await api.put<Rule>(`/notifications/rules/${ruleId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-rule', ruleId] })
      qc.invalidateQueries({ queryKey: ['notif-rules'] })
      toast.success('Saved')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const testMut = useMutation({
    mutationFn: async () =>
      (await api.post<{ enqueued: boolean; event: string }>(`/notifications/rules/${ruleId}/test/`)).data,
    onSuccess: () => {
      toast.success('Test fired — check your bell icon in a few seconds')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/notifications/rules/${ruleId}/`),
    onSuccess: () => {
      toast.success('Rule deleted')
      navigate('/admin/notifications/rules')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (ruleQ.isLoading || !draft) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (ruleQ.isError) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Rule not found.</p>
          <Link to="/admin/notifications/rules" className="text-violet-600 hover:underline">← Back to rules</Link>
        </div>
      </AdminLayout>
    )
  }

  function save() {
    if (!draft) return
    let filter_json: any = {}
    try {
      filter_json = filterJsonRaw.trim() ? JSON.parse(filterJsonRaw) : {}
    } catch {
      toast.error('Filter JSON is not valid JSON')
      return
    }
    saveMut.mutate({
      ...draft,
      filter_json,
    })
  }

  function toggleChannel(channel: string) {
    if (!draft) return
    const next = draft.channels.includes(channel)
      ? draft.channels.filter((c) => c !== channel)
      : [...draft.channels, channel]
    setDraft({ ...draft, channels: next })
  }

  const events = eventsQ.data || []
  const users = usersQ.data || []
  const roles = rolesQ.data || []
  const event = events.find((e) => e.id === draft.trigger_event)
  const placeholders = event?.placeholders || []

  return (
    <AdminLayout>
      {dialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/notifications/rules" className="text-muted-foreground hover:text-foreground hover:underline">← Rules</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{draft.name}</h2>
            <div className="text-sm text-muted-foreground mt-0.5">
              {draft.trigger_event_category} · <span className="font-mono">{draft.trigger_event_code}</span>
              {draft.is_system && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">SYSTEM</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
              <Play className="h-3 w-3 mr-1" /> {testMut.isPending ? 'Firing…' : 'Test fire'}
            </Button>
            <Button onClick={save} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={draft.is_system}
                  onClick={() => !draft.is_system && confirm(
                    `rule "${draft.name}"`,
                    () => deleteMut.mutate(),
                  )}
                  className={!draft.is_system ? 'text-destructive' : ''}
                >
                  {draft.is_system ? 'Delete (system rule)' : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl">
        {/* LEFT: rule meta + recipients + channels */}
        <div className="lg:col-span-1 space-y-4">
          <Section title="Rule">
            <Field>
              <FieldLabel htmlFor="rl-name">Name</FieldLabel>
              <Input id="rl-name" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="rl-desc">Description</FieldLabel>
              <Textarea id="rl-desc" rows={2} value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Why does this rule exist? Who manages it?" />
            </Field>
            <Field>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                <strong>Active</strong>
              </label>
              <FieldDescription>
                When inactive, this rule is skipped — no notifications get generated when its event fires.
              </FieldDescription>
            </Field>
          </Section>

          <Section title="Recipients">
            <Field>
              <FieldLabel htmlFor="rl-kind">Kind</FieldLabel>
              <SearchableSelect
                id="rl-kind"
                value={draft.recipient_kind}
                onChange={(v) => setDraft({ ...draft, recipient_kind: v as Rule['recipient_kind'] })}
                placeholder="—"
                options={[
                  { value: 'USER', label: 'Specific users' },
                  { value: 'ROLE', label: 'Users with a role' },
                  { value: 'DYNAMIC', label: 'Dynamic (computed)' },
                ]}
              />
            </Field>
            {draft.recipient_kind === 'USER' && (
              <Field>
                <FieldLabel>Users</FieldLabel>
                <MultiPicker
                  selected={draft.recipient_users}
                  onChange={(ids) => setDraft({ ...draft, recipient_users: ids })}
                  options={users.map((u) => ({
                    id: u.id,
                    label: ([u.first_name, u.last_name].filter(Boolean).join(' ') || u.username),
                    sub: u.email,
                  }))}
                  placeholder="Add user…"
                />
              </Field>
            )}
            {draft.recipient_kind === 'ROLE' && (
              <Field>
                <FieldLabel>Roles</FieldLabel>
                <MultiPicker
                  selected={draft.recipient_roles}
                  onChange={(ids) => setDraft({ ...draft, recipient_roles: ids })}
                  options={roles.map((r) => ({ id: r.id, label: r.name }))}
                  placeholder="Add role…"
                />
              </Field>
            )}
            {draft.recipient_kind === 'DYNAMIC' && (
              <Field>
                <FieldLabel htmlFor="rl-resolver">Resolver</FieldLabel>
                <Input id="rl-resolver" value={draft.recipient_resolver}
                  onChange={(e) => setDraft({ ...draft, recipient_resolver: e.target.value })}
                  placeholder="e.g. actor_manager OR event_field:inviter_user_id" />
                <FieldDescription>
                  Pick one of:
                  <ul className="list-disc list-inside mt-1 text-xs">
                    {RESOLVERS.map((r) => <li key={r.value}><code className="font-mono">{r.value}</code> — {r.label}</li>)}
                  </ul>
                </FieldDescription>
              </Field>
            )}
          </Section>

          <Section title="Channels">
            <Field>
              {CHANNELS.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                  <input type="checkbox"
                    checked={draft.channels.includes(c.value)}
                    onChange={() => toggleChannel(c.value)} />
                  {c.label}
                </label>
              ))}
              <FieldDescription>
                Pick at least one. Email channel requires Email Settings configured + active.
              </FieldDescription>
            </Field>
          </Section>
        </div>

        {/* RIGHT: templates + filter + placeholder cheatsheet */}
        <div className="lg:col-span-2 space-y-4">
          <Section title="Templates">
            <Field>
              <FieldLabel htmlFor="rl-subject">Subject</FieldLabel>
              <Input id="rl-subject" value={draft.subject_template}
                onChange={(e) => setDraft({ ...draft, subject_template: e.target.value })}
                placeholder="e.g. {recipient_name} accepted your invitation" />
            </Field>
            <Field>
              <FieldLabel htmlFor="rl-text">Plain-text body</FieldLabel>
              <Textarea id="rl-text" rows={6} value={draft.body_text_template}
                onChange={(e) => setDraft({ ...draft, body_text_template: e.target.value })}
                className="font-mono text-sm"
                placeholder="Hi,&#10;{recipient_name} accepted your invitation to join as {role_name}." />
            </Field>
            <Field>
              <FieldLabel htmlFor="rl-html">HTML body (optional)</FieldLabel>
              <Textarea id="rl-html" rows={6} value={draft.body_html_template}
                onChange={(e) => setDraft({ ...draft, body_html_template: e.target.value })}
                className="font-mono text-sm"
                placeholder="<p>Hi,</p><p>...</p>" />
              <FieldDescription>
                Used for the email channel when present. The in-app bell shows the plain-text body.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="rl-link">Click-through link template</FieldLabel>
              <Input id="rl-link" value={draft.link_template}
                onChange={(e) => setDraft({ ...draft, link_template: e.target.value })}
                placeholder={event?.default_link_template || '/admin/...'} />
              <FieldDescription>
                In-app notifications navigate here on click. Defaults to the event's default link if blank.
              </FieldDescription>
            </Field>
          </Section>

          <Section title="Available placeholders">
            {placeholders.length === 0 ? (
              <p className="text-xs text-muted-foreground">This event declares no placeholders.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map((p) => (
                  <code key={p} className="font-mono text-xs bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">
                    {`{${p}}`}
                  </code>
                ))}
                <code className="font-mono text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{`{actor_name}`}</code>
                <code className="font-mono text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{`{actor_email}`}</code>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Plus actor placeholders ({`{actor_id}`}, {`{actor_name}`}, {`{actor_email}`}, {`{actor_username}`}) injected automatically.
            </p>
          </Section>

          <Section title="Filter (optional)">
            <Field>
              <FieldLabel htmlFor="rl-filter">filter_json</FieldLabel>
              <Textarea
                id="rl-filter" rows={4} value={filterJsonRaw}
                onChange={(e) => setFilterJsonRaw(e.target.value)}
                className="font-mono text-sm"
                placeholder='{"job_code": "JOB-001"}'
              />
              <FieldDescription>
                JSON object. Rule fires only when every key matches the corresponding context value (equality only in v1). Empty = always fire.
              </FieldDescription>
            </Field>
          </Section>
        </div>
      </div>
    </AdminLayout>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-card rounded-lg shadow p-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

// Reusable lightweight multi-picker used for users / roles.
function MultiPicker({
  selected, onChange, options, placeholder,
}: {
  selected: number[]
  onChange: (ids: number[]) => void
  options: { id: number; label: string; sub?: string }[]
  placeholder: string
}) {
  const selectedSet = new Set(selected)
  const available = options.filter((o) => !selectedSet.has(o.id))
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {selected.length === 0 ? (
          <span className="text-xs text-muted-foreground">No one selected yet.</span>
        ) : (
          options.filter((o) => selectedSet.has(o.id)).map((o) => (
            <span key={o.id}
              className="text-xs px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1 bg-violet-100 text-violet-700">
              {o.label}
              <button
                type="button"
                onClick={() => onChange(selected.filter((id) => id !== o.id))}
                className="hover:opacity-70 ml-1"
                aria-label={`Remove ${o.label}`}
              >×</button>
            </span>
          ))
        )}
      </div>
      <SearchableSelect
        value={NONE}
        onChange={(v) => v !== NONE && onChange([...selected, Number(v)])}
        placeholder={placeholder}
        options={[
          { value: NONE, label: placeholder },
          ...available.map((o) => ({
            value: String(o.id),
            label: o.label,
            hint: o.sub,
          })),
        ]}
      />
    </div>
  )
}
